import React, { useEffect, useState, useCallback } from 'react';
import { Card, Badge, Row, Col } from 'react-bootstrap';
import './HealthScore.css'; // Ensure this CSS file is correctly linked

const HealthScore = ({ rating = 0, explanation = "No explanation available." }) => {
    const [animatedDisplayRating, setAnimatedDisplayRating] = useState(0);
    const [circumference, setCircumference] = useState(0);
    const [strokeDashoffset, setStrokeDashoffset] = useState(0);

    const RADIUS = 50;
    const STROKE_WIDTH = 10;

    useEffect(() => {
        const circ = 2 * Math.PI * RADIUS;
        setCircumference(circ);
    }, []);

    const getScoreInfo = useCallback(() => {
        const numericRating = Number(rating);
        if (isNaN(numericRating) || numericRating < 0 || numericRating > 10) {
            return {
                color: 'secondary', icon: '?', label: 'N/A',
                bgClass: 'health-score-unknown', textClass: 'text-muted',
                circleStroke: '#6c757d'
            };
        }
        if (numericRating >= 7) {
            return {
                color: 'success', icon: '👍', label: 'Good Choice',
                bgClass: 'health-score-high', textClass: 'text-success',
                circleStroke: '#198754'
            };
        } else if (numericRating >= 4) {
            return {
                color: 'warning', icon: '⚠️', label: 'Use with Caution',
                bgClass: 'health-score-medium', textClass: 'text-warning',
                circleStroke: '#ffc107'
            };
        } else {
            return {
                color: 'danger', icon: '👎', label: 'Not Recommended',
                bgClass: 'health-score-low', textClass: 'text-danger',
                circleStroke: '#dc3545'
            };
        }
    }, [rating]);

    const scoreInfo = getScoreInfo();

    useEffect(() => {
        const numericRating = Number(rating);
        if (isNaN(numericRating) || numericRating < 0 || numericRating > 10) {
            setAnimatedDisplayRating(0);
            setStrokeDashoffset(circumference);
            return;
        }

        const targetRating = numericRating;
        const targetPercentage = targetRating / 10;
        const targetOffset = circumference - (targetPercentage * circumference);

        setAnimatedDisplayRating(0);
        setStrokeDashoffset(circumference);

        let start = null;
        const duration = 1000;

        const step = (timestamp) => {
            if (!start) start = timestamp;
            const progress = Math.min((timestamp - start) / duration, 1);
            setAnimatedDisplayRating(parseFloat((progress * targetRating).toFixed(1)));
            setStrokeDashoffset(circumference - (progress * targetPercentage * circumference));
            if (progress < 1) {
                requestAnimationFrame(step);
            } else {
                setAnimatedDisplayRating(parseFloat(targetRating.toFixed(1)));
                setStrokeDashoffset(targetOffset);
            }
        };
        const animationFrameId = requestAnimationFrame(step);
        return () => cancelAnimationFrame(animationFrameId);
    }, [rating, circumference]);

    const svgSize = (RADIUS + STROKE_WIDTH) * 2;

    return (
        <Card className={`health-score-component shadow-sm mb-4 ${scoreInfo.bgClass}`}>
            {/* ADDED Card.Header for the title */}
            <Card.Header as="h5" className="bg-light-alt text-primary-alt border-bottom fw-bold p-3 health-score-card-header">
                {/* You can add an icon here too if you like */}
                {/* <FontAwesomeIcon icon={faHeartbeat} className="me-2" /> */}
                Health Score
            </Card.Header>
            <Card.Body className="p-md-4 p-3">
                <Row className="align-items-center g-3 g-md-4">
                    <Col xs={12} md={4} className="d-flex justify-content-center align-items-center">
                        <div className="radial-progress-container">
                            <svg
                                className="radial-progress-svg"
                                width={svgSize}
                                height={svgSize}
                                viewBox={`0 0 ${svgSize} ${svgSize}`}
                            >
                                <circle
                                    className="radial-progress-background"
                                    stroke="#e6e6e6"
                                    cx={svgSize / 2}
                                    cy={svgSize / 2}
                                    r={RADIUS}
                                    strokeWidth={STROKE_WIDTH}
                                    fill="transparent"
                                />
                                <circle
                                    className="radial-progress-bar"
                                    stroke={scoreInfo.circleStroke}
                                    cx={svgSize / 2}
                                    cy={svgSize / 2}
                                    r={RADIUS}
                                    fill="transparent"
                                    strokeWidth={STROKE_WIDTH}
                                    strokeDasharray={circumference}
                                    strokeDashoffset={strokeDashoffset}
                                    strokeLinecap="round"
                                    transform={`rotate(-90 ${svgSize / 2} ${svgSize / 2})`}
                                />
                                <text
                                    x="50%"
                                    y="50%"
                                    dy=".3em"
                                    textAnchor="middle"
                                    className="radial-progress-text"
                                    fill={scoreInfo.circleStroke}
                                >
                                    {animatedDisplayRating.toFixed(1)}
                                </text>
                            </svg>
                        </div>
                    </Col>

                    <Col xs={12} md={8}>
                        {/* The Badge now acts as the primary label next to the chart visualization */}
                        <div className="text-center text-md-start mb-3"> {/* Centered on small, start on md */}
                            <Badge pill bg={scoreInfo.color} className="px-3 py-2 fs-5 pulse-animation health-score-main-badge">
                                {scoreInfo.icon} {scoreInfo.label}
                            </Badge>
                        </div>

                        <div className="explanation-box p-3 rounded bg-light border shadow-sm">
                            <h6 className={`${scoreInfo.textClass} fw-bold mb-1`}>Expert Analysis:</h6>
                            <p className="mb-0 small text-secondary">{explanation}</p>
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

export default HealthScore;